/**
 * Silent re-authentication for Azure Static Web Apps.
 *
 * SWA issues a `StaticWebAppsAuthCookie` fixed at roughly 8 hours. It does not
 * slide forward with use, and SWA has no `/.auth/refresh` endpoint, so there is
 * no way to lengthen the session — see Azure/static-web-apps issue #761.
 *
 * The identity provider (Google, Facebook, …) does keep its own long-lived
 * session, though. So once the SWA cookie lapses we can bounce the visitor back
 * through `/.auth/login/<provider>`, which normally returns straight to the page
 * they were on with no prompt at all. This module remembers which provider they
 * used and performs that bounce.
 *
 * The bounce is deliberately NOT attempted on public pages — only on protected
 * routes, on the login page, and after an API call returns 401. That keeps
 * unexpected full-page redirects off the parts of the site anyone can read.
 */

const PROVIDER_KEY = 'tgc_auth_provider';
const RETURN_KEY = 'tgc_auth_return_to';
const ATTEMPT_KEY = 'tgc_auth_reauth_attempt';

/**
 * Don't bounce again within this window. If the provider can't sign the visitor
 * in without interaction, the bounce lands us back here with no session — this
 * cooldown is what stops that becoming a redirect loop.
 */
const RETRY_COOLDOWN_MS = 60_000;

const PROTECTED_PREFIXES = ['/dashboard', '/my-garden'];

/** Thrown by API helpers when a request comes back 401. */
export class SessionExpiredError extends Error {
  constructor(path: string) {
    super(`Session expired while requesting ${path}`);
    this.name = 'SessionExpiredError';
  }
}

// Storage is unavailable in some private-browsing modes; never let it throw.
function read(store: Storage, key: string): string | null {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

function write(store: Storage, key: string, value: string): void {
  try {
    store.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function remove(store: Storage, key: string): void {
  try {
    store.removeItem(key);
  } catch {
    /* ignore */
  }
}

function currentPath(): string {
  return window.location.pathname + window.location.search;
}

export function isProtectedPath(pathname = window.location.pathname): boolean {
  return PROTECTED_PREFIXES.some(p => pathname === p || pathname.startsWith(`${p}/`));
}

/** Record the provider a visitor signed in with, so we can reuse it later. */
export function rememberProvider(provider: string): void {
  if (!provider || provider === 'demo') return;
  write(localStorage, PROVIDER_KEY, provider);
}

/** Called once we know the session is good, to reset the loop guard. */
export function markSessionActive(): void {
  remove(sessionStorage, ATTEMPT_KEY);
}

/**
 * Clear everything the silent bounce relies on. Must be called on explicit
 * sign-out, otherwise the next visit would sign the user straight back in.
 */
export function forgetSession(): void {
  remove(localStorage, PROVIDER_KEY);
  remove(localStorage, RETURN_KEY);
  remove(sessionStorage, ATTEMPT_KEY);
}

/**
 * Remember where a signed-in visitor was. When SWA's server-side `allowedRoles`
 * check rejects a page load it redirects to /login, discarding the original URL,
 * so this is how we know where to put them back.
 */
export function rememberReturnPath(path: string): void {
  write(localStorage, RETURN_KEY, path);
}

/**
 * Redirect through the remembered identity provider to pick up a fresh session.
 *
 * Returns true if a redirect was started — callers should then treat the page as
 * navigating away and not render a signed-out state. Returns false if there is
 * nothing to try (no remembered provider, dev mode) or if we bounced too
 * recently, in which case the caller should fall back to showing the login page.
 */
export function attemptSilentReauth(returnTo?: string): boolean {
  if (import.meta.env.DEV) return false;

  const provider = read(localStorage, PROVIDER_KEY);
  if (!provider) return false;

  const lastAttempt = Number(read(sessionStorage, ATTEMPT_KEY) ?? 0);
  if (lastAttempt && Date.now() - lastAttempt < RETRY_COOLDOWN_MS) return false;
  write(sessionStorage, ATTEMPT_KEY, String(Date.now()));

  const target =
    returnTo ??
    (isProtectedPath() ? currentPath() : read(localStorage, RETURN_KEY) ?? '/dashboard');

  window.location.replace(
    `/.auth/login/${encodeURIComponent(provider)}` +
      `?post_login_redirect_uri=${encodeURIComponent(target)}`,
  );
  return true;
}
